import type { CustomChatMessage, CustomChatParticipantType } from '../types';

export type CustomChatMessageGroup = {
  id: string;
  messages: CustomChatMessage[];
  senderKey: string;
  senderName: string;
};

const getMessageTimestamp = (message: CustomChatMessage) => {
  const timestamp = new Date(message.created_at).getTime();

  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const sortCustomChatMessages = (messages: CustomChatMessage[]) =>
  [...messages].sort((firstMessage, secondMessage) => {
    const timeDiff =
      getMessageTimestamp(firstMessage) - getMessageTimestamp(secondMessage);

    if (timeDiff !== 0) return timeDiff;

    return Number(firstMessage.id) - Number(secondMessage.id);
  });

export const getCustomChatSenderName = (message: CustomChatMessage) =>
  message.sender_name?.replace(/\s+/g, ' ').trim() ||
  `${message.sender_type} ${message.sender_id}`;

export const getCustomChatSenderKey = (message: CustomChatMessage) =>
  `${message.sender_type}:${message.sender_id}`;

export const isCustomChatGeneralMessage = (message: CustomChatMessage) =>
  message.message_type?.toLowerCase() === 'general';

export const CUSTOM_CHAT_PARTICIPANT_ROLE_LABELS: Record<
  CustomChatParticipantType,
  string
> = {
  cleaner: 'Cleaner',
  client: 'Client',
  dashboard_user: 'Support',
};

export const getCustomChatSenderRoleLabel = (
  message?: Pick<CustomChatMessage, 'sender_type'> | null
) => {
  if (!message || message.sender_type === 'system') return null;

  return CUSTOM_CHAT_PARTICIPANT_ROLE_LABELS[message.sender_type] ?? null;
};

export const getCustomChatMessageDisplayText = (
  message?: Pick<CustomChatMessage, 'display_text' | 'text'> | null
) => {
  const displayText = message?.display_text;

  return displayText?.trim() ? displayText : message?.text ?? '';
};

export const getCustomChatTranslatedMessageInfo = (
  message?: Pick<
    CustomChatMessage,
    | 'display_text'
    | 'original_text'
    | 'sender_type'
    | 'source_language'
    | 'text'
    | 'translation_status'
  > | null
) => {
  if (!message || message.sender_type !== 'cleaner') return null;

  const translationStatus = message.translation_status?.trim().toLowerCase();
  if (translationStatus && translationStatus !== 'completed') return null;

  const displayText = message.display_text?.trim();
  if (!displayText) return null;

  const originalText = message.original_text?.trim() || message.text?.trim();
  if (!originalText || originalText === displayText) return null;

  return {
    originalText,
    sourceLanguage: message.source_language?.trim() || null,
  };
};

export const groupCustomChatMessages = (
  messages: CustomChatMessage[]
): CustomChatMessageGroup[] =>
  messages.reduce<CustomChatMessageGroup[]>((groups, message) => {
    const senderKey = isCustomChatGeneralMessage(message)
      ? `general:${message.id}`
      : getCustomChatSenderKey(message);
    const previousGroup = groups[groups.length - 1];

    if (
      previousGroup?.senderKey === senderKey &&
      !isCustomChatGeneralMessage(message)
    ) {
      previousGroup.messages.push(message);
      return groups;
    }

    groups.push({
      id: `${senderKey}:${message.id}`,
      messages: [message],
      senderKey,
      senderName: getCustomChatSenderName(message),
    });

    return groups;
  }, []);
